import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import {
  CommonModalName,
  StudioModalName,
} from '@cartwhl/common/stores/navigationStore';
import { getEnvironment } from '@cartwhl/common/utils/getEnvironment';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import ArrowUpRightIcon from '@cartwhl/ui/icons/ArrowUpRightIcon';
import HelpIcon from '@cartwhl/ui/icons/HelpIcon';
import InfoIcon from '@cartwhl/ui/icons/InfoIcon';
import LinkIcon from '@cartwhl/ui/icons/LinkIcon';
import MailIcon from '@cartwhl/ui/icons/MailIcon';
import SettingsIcon from '@cartwhl/ui/icons/SettingsIcon';
import SpinIcon from '@cartwhl/ui/icons/SpinIcon';
import Button from '@cartwhl/ui/input/Button';

import { navigationStore, uiStore, userStore } from 'stores';

import { getWelcomeModalVariant } from 'components/modals/WelcomeModal';

export default function HelpMenu() {
  const { organizationInfo, userInfo } = useSnapshot(userStore.state);
  const supportText =
    userInfo.planType === 'free' ? 'Contact support' : 'Advanced support';
  const mailtoAddress =
    userInfo.planType === 'free'
      ? 'support@getcartwheel.com'
      : 'pros@getcartwheel.com';
  const isRealProduction = getEnvironment() === 'prod';
  const [isProdOverrideEnabled, setIsProdOverrideEnabled] = useState(false);
  const showWelcomeItem = getWelcomeModalVariant(organizationInfo) !== null;

  useEffect(() => {
    try {
      const override = localStorage.getItem('envOverride');
      setIsProdOverrideEnabled(override === 'prod');
    } catch (e) {
      // Ignore localStorage errors
    }
  }, []);

  const toggleProductionMode = () => {
    try {
      if (isProdOverrideEnabled) {
        localStorage.removeItem('envOverride');
      } else {
        localStorage.setItem('envOverride', 'prod');
      }
      setIsProdOverrideEnabled(!isProdOverrideEnabled);
      // Force a refresh to apply the changes across the app
      window.location.reload();
    } catch (e) {
      console.error('Failed to toggle production mode:', e);
    }
  };

  return (
    <div className="hidden h-full w-20 items-center justify-end md:flex">
      <Dropdown
        trigger={
          <Button
            variant="secondary"
            size="sm"
            icon={<HelpIcon />}
            alignIcon="end"
          >
            Help
          </Button>
        }
      >
        {showWelcomeItem && (
          <Dropdown.Item
            icon={<InfoIcon />}
            onClick={() => {
              navigationStore.setModal(StudioModalName.WELCOME);
            }}
          >
            Welcome
          </Dropdown.Item>
        )}
        <Dropdown.Item
          icon={<ArrowUpRightIcon />}
          href={'https://docs.getcartwheel.com'}
        >
          Documentation
        </Dropdown.Item>
        <Dropdown.Item
          href={'https://discord.gg/TQS278evHH'}
          icon={<LinkIcon />}
        >
          Help on Discord
        </Dropdown.Item>
        <Dropdown.Item
          icon={<MailIcon />}
          href={`mailto:${mailtoAddress}?subject=Help%20with%20Studio`}
        >
          {supportText}
        </Dropdown.Item>
        <Dropdown.Item
          icon={<SpinIcon />}
          onClick={() => {
            navigationStore.setModal(CommonModalName.EXPLAIN_SPINS);
          }}
        >
          What are Spins?
        </Dropdown.Item>
        <Dropdown.Item
          icon={<HotkeysIcon />}
          onClick={uiStore.toggleHotkeyInfoOpen}
        >
          Hotkeys
        </Dropdown.Item>
        {!isRealProduction && (
          <Dropdown.Item icon={<SettingsIcon />} onClick={toggleProductionMode}>
            {isProdOverrideEnabled ? 'Disable' : 'Enable'} Production Mode
          </Dropdown.Item>
        )}
      </Dropdown>
    </div>
  );
}

const HotkeysIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      fill="currentColor"
      className={className}
      viewBox="0 0 256 256"
      height="20"
      width="20"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M184,104v32a8,8,0,0,1-8,8H99.31l10.35,10.34a8,8,0,0,1-11.32,11.32l-24-24a8,8,0,0,1,0-11.32l24-24a8,8,0,0,1,11.32,11.32L99.31,128H168V104a8,8,0,0,1,16,0Zm48-48V200a16,16,0,0,1-16,16H40a16,16,0,0,1-16-16V56A16,16,0,0,1,40,40H216A16,16,0,0,1,232,56ZM216,200V56H40V200H216Z"></path>
    </svg>
  );
};
